// UPS Reconciliation — admin page. Mirrors FedexReconciliation.jsx.
//
// UPS emails a password-protected ZIP containing a PDF tax invoice; upload it
// (or the bare PDF) on the Upload tab and services/ups_parser.py parses it,
// matches each 1Z waybill to an order, and writes a reconciliation row.

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Upload, AlertTriangle, AlertCircle, FileText, GitMerge, CheckCircle2, XCircle, Ban, Package } from 'lucide-react'
import {
  getUpsReconciliations, updateUpsReconciliationResolution, assignUpsReconciliation,
  getUpsInvoices, getUpsInvoiceLines, uploadUpsInvoice, getAdminOrders,
} from '../../lib/api'
import { useToast, ToastContainer } from '../../components/Toast'

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`

function TabButton({ id, activeTab, setActiveTab, icon: Icon, children }) {
  const isActive = activeTab === id
  return (
    <button
      onClick={() => setActiveTab(id)}
      className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
        isActive
          ? 'border-blue-500 text-blue-600'
          : 'border-transparent text-slate-500 hover:text-slate-700'
      }`}
    >
      <Icon size={14} />
      {children}
    </button>
  )
}

function StatTile({ label, value }) {
  return (
    <div className="card p-4">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-bold text-slate-800 mt-1 tabular-nums">{value}</p>
    </div>
  )
}

const FILTERS = [
  { key: 'all',       label: 'All',        params: {} },
  { key: 'pending',   label: 'Pending',    params: { resolution: 'pending' } },
  { key: 'flagged',   label: 'Flagged',    params: { flagged: true } },
  { key: 'unmatched', label: 'Unmatched',  params: { matched: false } },
]

function resolutionBadge(res) {
  const map = {
    pending:     'bg-slate-100 text-slate-600',
    accepted:    'bg-emerald-50 text-emerald-700',
    disputed:    'bg-amber-50 text-amber-700',
    written_off: 'bg-slate-100 text-slate-500',
    dismissed:   'bg-slate-100 text-slate-400',
  }
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${map[res] || 'bg-slate-100 text-slate-600'}`}>
      {(res || 'pending').replace('_', ' ')}
    </span>
  )
}

function ReconciliationTab() {
  const qc = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()
  const [showOnly, setShowOnly] = useState('all')
  const params = FILTERS.find(f => f.key === showOnly)?.params || {}

  // `error` is destructured deliberately: with only `data: rows = []` a failed
  // fetch reports 0 waybills, 0 flagged and $0.00 variance — a clean
  // reconciliation rather than one nobody could read.
  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ['ups-recon', showOnly],
    queryFn: () => getUpsReconciliations(params),
  })

  const resolveMut = useMutation({
    mutationFn: ({ reconId, resolution }) => updateUpsReconciliationResolution(reconId, resolution),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ups-recon'] }); addToast({ type: 'success', message: 'Updated.' }) },
    onError: (e) => addToast({ type: 'error', message: e.response?.data?.detail || 'Failed.' }),
  })

  const counts = {
    total:    rows.length,
    matched:  rows.filter(r => r.order_id).length,
    flagged:  rows.filter(r => r.flagged).length,
    variance: rows.reduce((s, r) => s + (Math.abs(Number(r.variance_abs)) || 0), 0),
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map(({ key, label }) => (
          <button key={key} onClick={() => setShowOnly(key)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              showOnly === key ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}>
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Total waybills</p>
          <p className="text-2xl font-bold text-slate-800">{error ? '—' : counts.total}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Matched</p>
          <p className="text-2xl font-bold text-emerald-600">{error ? '—' : counts.matched}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Flagged</p>
          <p className={`text-2xl font-bold ${counts.flagged > 0 ? 'text-amber-600' : 'text-slate-800'}`}>{error ? '—' : counts.flagged}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Variance (Σ)</p>
          <p className="text-2xl font-bold text-slate-800">{error ? '—' : money(counts.variance)}</p>
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
            <Loader2 size={18} className="animate-spin" /><span className="text-sm">Loading…</span>
          </div>
        ) : error ? (
          /* Added case: `rows` defaults to [], so a failed fetch would otherwise
             fall through to "No reconciliations match" — a clean reconciliation
             rather than one nobody could read. */
          <div className="flex flex-col items-center justify-center py-16 gap-2 px-4">
            <AlertTriangle size={32} className="text-red-400" />
            <p className="text-sm font-medium text-red-700">Couldn't load reconciliations</p>
            <p className="text-xs text-red-600 text-center max-w-md">
              This is a load failure, not an empty result — waybills may exist but aren't
              shown, and the figures above are not real totals.
            </p>
            <p className="text-xs text-slate-400 font-mono text-center max-w-md break-all">
              {error?.response?.data?.detail || error?.message || 'Unknown error'}
            </p>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <GitMerge size={32} className="text-slate-300" />
            <p className="text-sm font-medium text-slate-600">No reconciliations match</p>
            <p className="text-xs text-slate-400">Try a different filter, or ingest an invoice in the Upload tab.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  {['Tracking #','Order #','Client'].map(h => (
                    <th key={h} className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                  ))}
                  {['UPS','ShipStation','Variance'].map(h => (
                    <th key={h} className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                  ))}
                  {['Match','Status','Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(r => (
                  <tr key={r.id} className={`hover:bg-slate-50 transition-colors ${r.flagged ? 'bg-amber-50/30' : ''}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{r.tracking_number}</td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">
                      {r.order_number || <span className="text-slate-400 italic">Unmatched</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{r.client_name || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-700">{money(r.ups_shipping_total)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-600">{money(r.shipstation_cost)}</td>
                    <td className={`px-4 py-3 text-right tabular-nums font-semibold ${
                      Number(r.variance_abs) > 0 ? 'text-red-600'
                        : Number(r.variance_abs) < 0 ? 'text-emerald-600' : 'text-slate-500'
                    }`}>
                      {r.flagged && (
                        <span title={r.flag_reason || 'Flagged for review'}>
                          <AlertTriangle size={11} className="inline mr-1 text-amber-500" />
                        </span>
                      )}
                      {money(r.variance_abs)}
                      {r.variance_pct != null && (
                        <span className="text-xs text-slate-400 ml-1">({Number(r.variance_pct).toFixed(1)}%)</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {r.match_method && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{r.match_method}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{resolutionBadge(r.resolution)}</td>
                    <td className="px-4 py-3">
                      {r.resolution === 'pending' && (
                        <div className="flex items-center gap-1">
                          {r.order_id && (
                            <>
                              <button onClick={() => resolveMut.mutate({ reconId: r.id, resolution: 'accepted' })}
                                disabled={resolveMut.isPending}
                                className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100">Accept</button>
                              <button onClick={() => resolveMut.mutate({ reconId: r.id, resolution: 'disputed' })}
                                disabled={resolveMut.isPending}
                                className="text-xs px-2 py-1 rounded bg-amber-50 text-amber-700 hover:bg-amber-100">Dispute</button>
                            </>
                          )}
                          <button onClick={() => resolveMut.mutate({ reconId: r.id, resolution: 'dismissed' })}
                            disabled={resolveMut.isPending}
                            title="Clear this waybill from the queue without billing it — e.g. a UPS credit/adjustment that can't reconcile."
                            className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-600 hover:bg-slate-200">Dismiss</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ToastContainer toasts={toasts} onClose={removeToast} onRemove={removeToast} />
    </div>
  )
}

function ExceptionsTab() {
  const qc = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['ups-recon', 'unmatched'],
    queryFn: () => getUpsReconciliations({ matched: false }),
  })
  const assign = useMutation({
    mutationFn: ({ id, orderId }) => assignUpsReconciliation(id, orderId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ups-recon'] }); addToast({ type: 'success', message: 'Assigned.' }) },
    onError: (e) => addToast({ type: 'error', message: e.response?.data?.detail || 'Assign failed.' }),
  })

  return (
    <div className="space-y-4">
      <div className="card border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 space-y-1">
        <p className="font-medium">Unmatched waybills</p>
        <p className="text-amber-700 text-xs">
          UPS billed these tracking numbers but no order carries them — a manual label, a reused
          number, or an order that never synced. Search an order below to link it; the cost then
          reconciles like any other waybill.
        </p>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
            <Loader2 size={18} className="animate-spin" /><span className="text-sm">Loading…</span>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <CheckCircle2 size={32} className="text-emerald-300" />
            <p className="text-sm font-medium text-slate-600">Nothing unmatched</p>
            <p className="text-xs text-slate-400">Every UPS waybill is linked to an order.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map(r => (
              <ExceptionRow key={r.id} row={r} onAssign={(orderId) => assign.mutate({ id: r.id, orderId })} />
            ))}
          </div>
        )}
      </div>
      <ToastContainer toasts={toasts} onClose={removeToast} onRemove={removeToast} />
    </div>
  )
}

function ExceptionRow({ row, onAssign }) {
  const [q, setQ] = useState('')
  const { data: matches } = useQuery({
    queryKey: ['ups-order-search', q],
    queryFn: () => getAdminOrders({ search: q, limit: 5 }),
    enabled: q.length >= 3,
  })
  const list = Array.isArray(matches) ? matches : (matches?.orders || matches?.data || [])

  return (
    <div className="p-4 flex flex-wrap items-center gap-3 hover:bg-slate-50 transition-colors">
      <div className="min-w-0">
        <p className="font-mono text-xs text-slate-700">{row.tracking_number}</p>
        <p className="text-xs text-slate-400 mt-0.5">
          {money(row.ups_shipping_total)} · {row.match_method || 'unmatched'}
        </p>
      </div>
      <div className="flex-1 min-w-[200px]">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search order number…"
          className="input text-sm py-1.5" />
      </div>
      <div className="flex gap-1 flex-wrap">
        {list.slice(0, 5).map(o => (
          <button key={o.id} onClick={() => onAssign(o.id)}
            className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 font-mono">
            {o.order_number}
          </button>
        ))}
        {q.length >= 3 && list.length === 0 && (
          <span className="text-xs text-slate-400 self-center">No orders match</span>
        )}
      </div>
    </div>
  )
}

function InvoiceRow({ inv }) {
  const [open, setOpen] = useState(false)
  const { data: lines = [], isLoading } = useQuery({
    queryKey: ['ups-invoice-lines', inv.id],
    queryFn: () => getUpsInvoiceLines(inv.id),
    enabled: open,
  })
  return (
    <>
      <tr onClick={() => setOpen(o => !o)} className="cursor-pointer hover:bg-slate-50">
        <td className="px-3 py-2 text-slate-400">{open ? '▾' : '▸'}</td>
        <td className="px-3 py-2 font-mono text-xs">{inv.invoice_number}</td>
        <td className="px-3 py-2">
          <span className="badge bg-slate-100 text-slate-600">{inv.invoice_type || '—'}</span>
        </td>
        <td className="px-3 py-2 text-slate-600">{inv.invoice_date || '—'}</td>
        <td className="px-3 py-2 text-slate-500 text-xs">{inv.account_number || '—'}</td>
        <td className="px-3 py-2 text-right tabular-nums font-medium">{money(inv.statement_total)}</td>
        <td className="px-3 py-2">
          <span className={`badge ${inv.parse_status === 'parsed'
            ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
            {inv.parse_status}
          </span>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} className="px-3 pb-3 bg-slate-50/60">
            {isLoading ? (
              <div className="flex justify-center py-4"><Loader2 size={16} className="animate-spin text-slate-400" /></div>
            ) : lines.length === 0 ? (
              <p className="text-xs text-slate-400 py-3 text-center">No waybill lines stored.</p>
            ) : (
              <table className="w-full text-xs mt-2">
                <thead className="text-slate-400 uppercase tracking-wider">
                  <tr className="text-left">
                    <th className="px-2 py-1">Tracking</th><th className="px-2 py-1">Ship date</th>
                    <th className="px-2 py-1">Service</th><th className="px-2 py-1">Destination</th>
                    <th className="px-2 py-1 text-right">Net</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {lines.map(l => (
                    <tr key={l.id}>
                      <td className="px-2 py-1.5 font-mono">{l.tracking_number}</td>
                      <td className="px-2 py-1.5">{l.ship_date || '—'}</td>
                      <td className="px-2 py-1.5">{l.service_type || '—'}</td>
                      <td className="px-2 py-1.5">{l.recipient_address || '—'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-medium">{money(l.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

function InvoicesTab() {
  const { data: invoices = [], isLoading } = useQuery({ queryKey: ['ups-invoices'], queryFn: () => getUpsInvoices() })
  const total = invoices.reduce((s, i) => s + (Number(i.statement_total) || 0), 0)
  const rejected = invoices.filter(i => i.parse_status !== 'parsed').length

  if (isLoading) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-slate-400" /></div>

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatTile label="Invoices" value={invoices.length} />
        <StatTile label="Total value" value={money(total)} />
        <StatTile label="Not parsed" value={rejected} />
      </div>

      {invoices.length === 0 ? (
        <div className="card p-10 text-center">
          <Package size={28} className="mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-500">No UPS invoices ingested yet.</p>
          <p className="text-xs text-slate-400 mt-1">Upload one on the Upload tab.</p>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500">
              <tr className="text-left">
                <th className="px-3 py-2 w-6"></th>
                <th className="px-3 py-2">Invoice #</th><th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Date</th><th className="px-3 py-2">Account</th>
                <th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {invoices.map(i => <InvoiceRow key={i.id} inv={i} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function UploadTab() {
  const qc = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()
  const [file, setFile] = useState(null)
  const [dragOver, setDragOver] = useState(false)
  const [result, setResult] = useState(null)   // persistent — a toast vanishes before it can be read
  const [error, setError] = useState(null)

  const ACCEPT = ['.pdf', '.zip']
  const ok = (f) => ACCEPT.some(x => f.name.toLowerCase().endsWith(x))

  const upload = useMutation({
    mutationFn: () => uploadUpsInvoice(file),
    onMutate: () => { setResult(null); setError(null) },
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['ups-invoices'] })
      qc.invalidateQueries({ queryKey: ['ups-recon'] })
      setResult(d); setFile(null)
      addToast({ type: 'success', message: `Ingested invoice ${d.invoice_number || ''}`.trim() })
    },
    onError: (e) => {
      const detail = e.response?.data?.detail
      setError(typeof detail === 'string' ? detail : (e.message || 'Upload failed.'))
    },
  })

  function onDrop(e) {
    e.preventDefault(); setDragOver(false)
    const f = Array.from(e.dataTransfer.files || []).find(ok)
    if (f) { setFile(f); setError(null); setResult(null) }
  }

  return (
    <div className="space-y-4">
      <div className="card border-blue-200 bg-blue-50 p-4 text-sm text-blue-700 space-y-1">
        <p className="font-medium">Manual upload</p>
        <p className="text-blue-600 text-xs">
          Drop the password-protected ZIP UPS emails (or the bare PDF) to ingest it immediately.
          The ZIP password is read from <span className="font-mono">UPS_INVOICE_ZIP_PASSWORD</span>.
          Waybills match to orders by their 1Z tracking number, and duplicates (by invoice number
          or file hash) are skipped automatically.
        </p>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`card flex flex-col items-center justify-center py-12 border-2 border-dashed transition-colors ${
          dragOver ? 'border-blue-400 bg-blue-50/50' : 'border-slate-200 bg-slate-50'
        }`}
      >
        <Upload size={32} className="text-slate-400 mb-3" />
        <p className="text-sm font-medium text-slate-700">Drop a UPS invoice here</p>
        <p className="text-xs text-slate-500 mt-1">ZIP or PDF · or</p>
        <label className="btn-secondary text-xs mt-2 cursor-pointer">
          Choose file
          <input type="file" className="hidden" accept=".pdf,.PDF,.zip,.ZIP"
            onChange={(e) => { const f = e.target.files?.[0]; if (f && ok(f)) { setFile(f); setError(null); setResult(null) } e.target.value = '' }} />
        </label>
      </div>

      {file && (
        <div className="card p-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={15} className="text-slate-400 shrink-0" />
            <span className="text-sm text-slate-700 truncate">{file.name}</span>
            <span className="text-xs text-slate-400 shrink-0">{(file.size / 1024).toFixed(0)} KB</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setFile(null)} className="text-xs text-slate-400 hover:text-red-500">Remove</button>
            <button onClick={() => upload.mutate()} disabled={upload.isPending} className="btn-primary text-xs py-1.5 px-3">
              {upload.isPending ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              Upload &amp; reconcile
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="card border-red-200 bg-red-50 p-4">
          <p className="text-sm font-semibold text-red-800 flex items-center gap-1.5">
            <AlertCircle size={15} /> Ingest failed
          </p>
          <p className="text-sm text-red-700 mt-1 whitespace-pre-wrap">{error}</p>
        </div>
      )}

      {result && (
        <div className="card p-4">
          <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <CheckCircle2 size={15} className="text-emerald-600" />
            {result.status === 'duplicate' ? 'Already ingested' : 'Ingested'}
            {result.invoice_number ? ` — ${result.invoice_number}` : ''}
          </p>
          {result.status === 'duplicate' ? (
            <p className="text-sm text-slate-500 mt-1">{result.message}</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
              {[['Lines', result.lines], ['Matched', result.matched],
                ['Unmatched', result.unmatched], ['Adjustments', result.adjustments_applied]].map(([l, v]) => (
                <div key={l}>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{l}</p>
                  <p className="text-xl font-bold text-slate-800 tabular-nums">{v ?? 0}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ToastContainer toasts={toasts} onClose={removeToast} onRemove={removeToast} />
    </div>
  )
}

export default function UpsReconciliation() {
  const [activeTab, setActiveTab] = useState('reconciliation')

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-800">UPS Reconciliation</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          Match UPS invoice waybills against ShipStation costs for client billing accuracy and dispute evidence.
        </p>
      </div>

      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        <TabButton id="upload"         activeTab={activeTab} setActiveTab={setActiveTab} icon={Upload}>Upload</TabButton>
        <TabButton id="invoices"       activeTab={activeTab} setActiveTab={setActiveTab} icon={FileText}>Invoices</TabButton>
        <TabButton id="reconciliation" activeTab={activeTab} setActiveTab={setActiveTab} icon={GitMerge}>Reconciliation</TabButton>
        <TabButton id="exceptions"     activeTab={activeTab} setActiveTab={setActiveTab} icon={AlertCircle}>Exceptions</TabButton>
      </div>

      {activeTab === 'upload'         && <UploadTab />}
      {activeTab === 'invoices'       && <InvoicesTab />}
      {activeTab === 'reconciliation' && <ReconciliationTab />}
      {activeTab === 'exceptions'     && <ExceptionsTab />}
    </div>
  )
}
